<script setup>
import { computed } from 'vue';
import { formatBytes } from 'shared/helpers/FileHelper';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  attachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['removeAttachment']);

const nonRecordedAudioAttachments = computed(() => {
  return props.attachments.filter(attachment => !attachment?.isRecordedAudio);
});

const recordedAudioAttachments = computed(() =>
  props.attachments.filter(attachment => attachment.isRecordedAudio)
);

const onRemoveAttachment = itemIndex => {
  emit(
    'removeAttachment',
    nonRecordedAudioAttachments.value
      .filter((_, index) => index !== itemIndex)
      .concat(recordedAudioAttachments.value)
  );
};

const formatFileSize = file => {
  const size = file.byte_size || file.size;
  return formatBytes(size, 0);
};

const isTypeImage = file => {
  const type = file.content_type || file.type;
  return type.includes('image');
};

const isTypeVideo = file => {
  const type = file.content_type || file.type || '';
  return type.includes('video');
};

const isTypeAudio = file => {
  const type = file.content_type || file.type || '';
  return type.includes('audio');
};

const fileName = file => {
  return file.filename || file.name;
};

const fileExtension = file => {
  const name = fileName(file) || '';
  const idx = name.lastIndexOf('.');
  return idx > -1 ? name.slice(idx + 1).toUpperCase() : '';
};
</script>

<template>
  <div class="flex flex-wrap max-h-[12.5rem]">
    <div
      v-for="(attachment, index) in nonRecordedAudioAttachments"
      :key="attachment.id"
      class="m-1 flex w-[15rem] items-center gap-1 rounded-md bg-n-slate-3 p-1"
    >
      <div class="max-w-[4rem] flex-shrink-0 w-6 flex items-center">
        <span
          v-if="isTypeImage(attachment.resource)"
          class="i-ph-camera w-6 h-6 text-lg text-left -top-px"
        />
        <span
          v-else-if="isTypeVideo(attachment.resource)"
          class="i-ph-video-camera w-6 h-6 text-lg text-left -top-px"
        />
        <span
          v-else-if="isTypeAudio(attachment.resource)"
          class="i-ph-music-notes w-6 h-6 text-lg text-left -top-px"
        />
        <span v-else class="i-ph-file w-6 h-6 text-lg text-left -top-px" />
      </div>
      <div class="max-w-3/5 min-w-[50%] overflow-hidden text-ellipsis">
        <span
          class="h-4 overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap"
        >
          {{ fileName(attachment.resource) }}
        </span>
        <span
          v-if="fileExtension(attachment.resource)"
          class="ml-1 px-1 py-[1px] text-[10px] rounded bg-n-slate-5 text-n-slate-11"
        >
          {{ fileExtension(attachment.resource) }}
        </span>
      </div>
      <div class="w-[30%] justify-center">
        <span class="overflow-hidden text-xs text-ellipsis whitespace-nowrap">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center">
        <Button
          ghost
          slate
          xs
          icon="i-lucide-x"
          type="button"
          @click="onRemoveAttachment(index)"
        />
      </div>
    </div>
  </div>
</template>
